import type { CreateTradeResponse } from 'apiConnector/secondary-market/shared/trade';
import type { Action } from 'state/actions';
import type { OrderActionType } from 'state/secondaryMarket/shared/actions';
import type { TradeMap, TradeState } from 'state/secondaryMarket/shared/trade/trade.model';
import { addOrReplaceKey, patchKey } from 'state/utils/collection';

export function tradeReducerFactory<TradeRes extends CreateTradeResponse, Map extends TradeMap<TradeRes>>(
  actionType: OrderActionType,
  initialTradeMap: () => Map,
  customReducer?: (state: Map, action: Action) => TradeMap<TradeRes>,
) {
  return (state: Map = initialTradeMap(), action: Action) => {
    switch (action.type) {
      case `SECONDARY_MARKET_${actionType}_TRADE_REQUESTED` as const:
        return addOrReplaceKey(state, action.tabId, {
          tradeStatus: 'TRADING' as const,
          tradeDate: undefined,
        } as TradeState<TradeRes>);
      case `SECONDARY_MARKET_${actionType}_TRADE_REJECTED` as const:
        return patchKey(
          state,
          action.tabId,
          () =>
            ({
              tradeStatus: 'TRADE_REJECTED' as const,
              errorMessage: action.error,
              timeout: action.timeout,
              tradeDate: undefined,
            }) as Partial<Map[string]>,
        );
      case `SECONDARY_MARKET_${actionType}_TRADE_FAILED` as const:
        return patchKey(
          state,
          action.tabId,
          () =>
            ({
              tradeStatus: 'TRADE_FAILED' as const,
              errorMessage: action.error,
              timeout: action.timeout,
            }) as Partial<Map[string]> as Partial<Map[string]>,
        );
      case `SECONDARY_MARKET_${actionType}_TRADE_RECEIVED` as const:
        return patchKey(
          state,
          action.tabId,
          () =>
            ({
              ...action.data,
              tradeStatus: 'TRADE_DONE' as const,
              errorMessage: undefined,
              timeout: undefined,
            }) as Partial<Map[string]>,
        );

      default:
        if (customReducer) {
          return customReducer(state, action);
        }
        return state;
    }
  };
}
